const PREFIXES = ['testid', 'name', 'text', 'css'];

export const parseTarget = (target) => {
    if (target === null || target === undefined || target === '') {
        return null;
    }

    if (typeof target === 'object') {
        const kind = PREFIXES.find(prefix => target[prefix] !== undefined);
        return kind ? {kind, value: String(target[kind])} : null;
    }

    const text = String(target);
    const separator = text.indexOf(':');

    if (separator > 0) {
        const kind = text.slice(0, separator);
        if (PREFIXES.includes(kind)) {
            return {kind, value: text.slice(separator + 1)};
        }
    }

    return {kind: 'testid', value: text};
};

const escapeValue = (value) => JSON.stringify(String(value)).slice(1, -1);

const isParsed = (value) => Boolean(value) &&
    typeof value === 'object' &&
    PREFIXES.includes(value.kind) &&
    typeof value.value === 'string';

export const toSelector = (target) => {
    const parsed = isParsed(target) ? target : parseTarget(target);

    if (!parsed) {
        return null;
    }

    switch (parsed.kind) {
        case 'testid':
            return `[data-testid="${escapeValue(parsed.value)}"]`;
        case 'name':
            return `[name="${escapeValue(parsed.value)}"]`;
        case 'css':
            return parsed.value;
        default:
            return null;
    }
};

const hidden = (element) => {
    if (element.hasAttribute('hidden') || element.getAttribute('aria-hidden') === 'true') {
        return true;
    }

    const style = element.ownerDocument.defaultView?.getComputedStyle(element);
    return style ? style.display === 'none' || style.visibility === 'hidden' : false;
};

const painted = (element) => {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
};

const best = (candidates) => candidates.find(element => !hidden(element) && painted(element)) ??
    candidates.find(element => !hidden(element)) ??
    candidates[0] ??
    null;

export const findTarget = (target, root = document) => {
    const parsed = isParsed(target) ? target : parseTarget(target);

    if (!parsed) {
        return null;
    }

    if (parsed.kind === 'text') {
        const wanted = parsed.value.trim().toLowerCase();
        const candidates = Array.from(
            root.querySelectorAll('button, a, [role="button"], [role="tab"], [role="menuitem"], label')
        ).filter(element => (element.textContent || '').trim().toLowerCase() === wanted);

        return best(candidates);
    }

    return best(Array.from(root.querySelectorAll(toSelector(parsed))));
};

export const waitForTarget = (target, {timeout = 10000, root = document} = {}) => {
    const found = findTarget(target, root);

    if (found) {
        return Promise.resolve(found);
    }

    return new Promise((resolve, reject) => {
        const observer = new MutationObserver(() => {
            const element = findTarget(target, root);
            if (element) {
                observer.disconnect();
                clearTimeout(timer);
                resolve(element);
            }
        });

        const timer = setTimeout(() => {
            observer.disconnect();
            reject(new Error(`Nothing on the page matches ${JSON.stringify(target)} after ${timeout}ms`));
        }, timeout);

        observer.observe(root === document ? document.documentElement : root, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-testid', 'name', 'style', 'class']
        });
    });
};
